import React, { useState, useRef, useEffect } from 'react';
import './BoltTorqueAndPreloadCalculator.css';

// ─── DATA ───
const UNC_SIZES = [
  { s: '#4-40', d: 0.112, dm: 0.0813, tpi: 40, At: 0.00604 },
  { s: '#6-32', d: 0.138, dm: 0.0997, tpi: 32, At: 0.00909 },
  { s: '#8-32', d: 0.164, dm: 0.1257, tpi: 32, At: 0.0140 },
  { s: '#10-24', d: 0.190, dm: 0.1389, tpi: 24, At: 0.0175 },
  { s: '1/4-20', d: 0.250, dm: 0.1887, tpi: 20, At: 0.0318 },
  { s: '5/16-18', d: 0.3125, dm: 0.2443, tpi: 18, At: 0.0524 },
  { s: '3/8-16', d: 0.375, dm: 0.2983, tpi: 16, At: 0.0775 },
  { s: '7/16-14', d: 0.4375, dm: 0.3499, tpi: 14, At: 0.1068 },
  { s: '1/2-13', d: 0.500, dm: 0.4001, tpi: 13, At: 0.1419 },
  { s: '9/16-12', d: 0.5625, dm: 0.4542, tpi: 12, At: 0.1820 },
  { s: '5/8-11', d: 0.625, dm: 0.5069, tpi: 11, At: 0.2260 },
  { s: '3/4-10', d: 0.750, dm: 0.6201, tpi: 10, At: 0.3340 },
  { s: '7/8-9', d: 0.875, dm: 0.7307, tpi: 9, At: 0.4620 },
  { s: '1-8', d: 1.000, dm: 0.8376, tpi: 8, At: 0.6060 },
  { s: '1 1/4-7', d: 1.250, dm: 1.0644, tpi: 7, At: 0.9690 },
  { s: '1 1/2-6', d: 1.500, dm: 1.2835, tpi: 6, At: 1.4100 },
];

const MATERIALS = [
  { g: 'SAE Grade 2 (≤¾")', sty: 57000, mpa: 393, note: 'Low-carbon steel' },
  { g: 'SAE Grade 5', sty: 92000, mpa: 634, note: 'Medium-carbon steel' },
  { g: 'SAE Grade 8', sty: 130000, mpa: 896, note: 'Alloy steel, most common' },
  { g: 'ASTM A193 B7', sty: 105000, mpa: 724, note: 'Alloy steel stud bolt' },
  { g: 'ISO 8.8', sty: 116000, mpa: 799, note: '≈ SAE Grade 5' },
  { g: 'ISO 10.9', sty: 152000, mpa: 1048, note: '≈ SAE Grade 8' },
  { g: 'ISO 12.9', sty: 174000, mpa: 1199, note: 'High-strength alloy' },
  { g: '304 Stainless', sty: 30000, mpa: 207, note: 'Corrosion resistant' },
  { g: '316 Stainless', sty: 30000, mpa: 207, note: 'Marine grade' },
  { g: 'Titanium Grade 5', sty: 128000, mpa: 883, note: 'Aerospace Ti-6Al-4V' },
];

const FRICTION = [
  { c: 'As-received, dry', ft: 0.15, fc: 0.15, kt: 0.20 },
  { c: 'Lightly oiled', ft: 0.12, fc: 0.12, kt: 0.16 },
  { c: 'Machine oil', ft: 0.10, fc: 0.10, kt: 0.13 },
  { c: 'Heavy zinc phosphate', ft: 0.08, fc: 0.08, kt: 0.10 },
  { c: 'Cadmium plated', ft: 0.07, fc: 0.07, kt: 0.09 },
  { c: 'PTFE / wax thread lube', ft: 0.06, fc: 0.06, kt: 0.08 },
  { c: 'Stainless on stainless', ft: 0.20, fc: 0.20, kt: 0.26 },
  { c: 'Galvanized', ft: 0.17, fc: 0.17, kt: 0.22 },
];

const FAQS = [
  {
    q: 'What is bolt preload?',
    a: "Bolt preload is the clamping force generated in a bolted joint when a bolt is tightened. It keeps mating parts pressed together and prevents joint separation under service loads. Preload is typically specified as a percentage of the bolt's tensile yield strength — the recommended range is 64%–77%.",
  },
  {
    q: 'What is the torque coefficient (K-factor / nut factor)?',
    a: 'The K-factor (nut factor) relates applied torque to the resulting preload via T = K × d × F. Its value depends on thread geometry and friction. A K = 0.20 default is widely used for as-received dry steel fasteners. Lubricated joints typically use K = 0.13–0.16.',
  },
  {
    q: 'How accurate is torque-controlled tightening?',
    a: 'Torque wrenches typically produce preload scatter of ±25% due to friction variability. The actual preload can range from 75% to 125% of the nominal target. More precise methods — stretch measurement, turn-of-nut, and direct tension indicators (DTIs) — can reduce uncertainty to ±10% or better.',
  },
  {
    q: 'What is embedment relaxation?',
    a: 'After initial installation, micro-asperities on bolt threads and joint surfaces plastically deform, causing the joint to settle. This embedment relaxation typically reduces preload by 5%–15%. The calculator uses a 10% default. Critical applications may require re-torquing after initial installation.',
  },
  {
    q: 'What thread standard does this calculator use?',
    a: 'This calculator is based on ASME B1.1 Unified National Thread Standard for UNC (Unified National Coarse) threads, with a thread half-angle of α = 30°. It is compatible with inch-system fasteners used in the United States and is the most widely used standard in mechanical engineering.',
  },
  {
    q: 'Why does friction affect torque so much?',
    a: 'In a typical bolted joint, approximately 40–50% of applied torque overcomes thread friction and another 40–50% overcomes collar (bearing surface) friction. Only the remaining ~10% actually stretches the bolt to create preload. This is why accurate friction data is critical — a small change in friction coefficient significantly changes the required torque.',
  },
];

// ─── HELPERS ───
const fmt = (n, d = 2) => (isNaN(n) ? '—' : n.toFixed(d));
const fmtComma = (n, d = 0) =>
  isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function BoltTorqueAndPreloadCalculator() {
  // ── INPUTS ──
  const [boltPreset, setBoltPreset] = useState('1/2-13');
  const [dNom, setDNom] = useState('0.500');
  const [dMinor, setDMinor] = useState('0.4001');
  const [tpi, setTpi] = useState('13');
  const [At, setAt] = useState('0.1419');
  const [materialPreset, setMaterialPreset] = useState('SAE Grade 8');
  const [Sty, setSty] = useState('130000');
  const [pctYld, setPctYld] = useState('0.67');
  const [frictionPreset, setFrictionPreset] = useState('As-received, dry');
  const [ft, setFt] = useState('0.15');
  const [fc, setFc] = useState('0.15');
  const [uncrt, setUncrt] = useState('0.25');
  const [relax, setRelax] = useState('0.10');

  // ── RESULTS ──
  const [results, setResults] = useState(null);
  const [toast, setToast] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef(null);

  // ── TABS & FAQ ──
  const [activeTab, setActiveTab] = useState('unc');
  const [openFaq, setOpenFaq] = useState(null);

  // Run default calculation on mount
  useEffect(() => {
    runCalculate('0.500', '0.4001', '13', '0.1419', '130000', '0.67', '0.15', '0.15', '0.25', '0.10');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── TOAST ──
  const showToast = (msg) => {
    setToast(msg);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2500);
  };

  // ── PRESET HANDLERS ──
  const applyBoltPreset = (val) => {
    setBoltPreset(val);
    const found = UNC_SIZES.find((u) => u.s === val);
    if (found) {
      setDNom(String(found.d));
      setDMinor(String(found.dm));
      setTpi(String(found.tpi));
      setAt(String(found.At));
    }
  };

  const applyMaterialPreset = (val) => {
    setMaterialPreset(val);
    const found = MATERIALS.find((m) => m.g === val);
    if (found) setSty(String(found.sty));
  };

  const applyFrictionPreset = (val) => {
    setFrictionPreset(val);
    const found = FRICTION.find((f) => f.c === val);
    if (found) {
      setFt(String(found.ft));
      setFc(String(found.fc));
    }
  };

  const applyUNC = (u) => {
    setDNom(String(u.d));
    setDMinor(String(u.dm));
    setTpi(String(u.tpi));
    setAt(String(u.At));
    setBoltPreset('');
    showToast(`✓ Applied ${u.d}" bolt — scroll up to calculate`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyMaterial = (m) => {
    setSty(String(m.sty));
    setMaterialPreset('');
    showToast(`✓ Applied ${m.sty.toLocaleString()} psi yield strength`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyFrictionRow = (f) => {
    setFt(String(f.ft));
    setFc(String(f.fc));
    setFrictionPreset('');
    showToast(`✓ Applied friction ft=${f.ft}, fc=${f.fc}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── CALCULATE ──
  const runCalculate = (
    _dNom = dNom,
    _dMinor = dMinor,
    _tpi = tpi,
    _At = At,
    _Sty = Sty,
    _pctYld = pctYld,
    _ft = ft,
    _fc = fc,
    _uncrt = uncrt,
    _relax = relax
  ) => {
    const vDNom = parseFloat(_dNom);
    const vDMinor = parseFloat(_dMinor);
    const vTpi = parseFloat(_tpi);
    const vAt = parseFloat(_At);
    const vSty = parseFloat(_Sty);
    const vPctYld = parseFloat(_pctYld);
    const vFt = parseFloat(_ft);
    const vFc = parseFloat(_fc);
    const vUncrt = parseFloat(_uncrt);
    const vRelax = parseFloat(_relax);

    if ([vDNom, vDMinor, vTpi, vAt, vSty, vPctYld, vFt, vFc].some(isNaN)) {
      showToast('⚠ Please fill in all required fields');
      return;
    }

    const P = 1 / vTpi;
    const rt = (vDNom + vDMinor) / 4;
    const rc = 0.625 * vDNom;
    const lam = Math.atan(P / (2 * Math.PI * rt));
    const alpha = (30 * Math.PI) / 180;

    const KT = (1 / vDNom) * (rt * (Math.tan(lam) + vFt / Math.cos(alpha)) + vFc * rc);
    const F_PL = vPctYld * vSty * vAt;
    const T_in = KT * vDNom * F_PL;
    const T_ft = T_in / 12;
    const T_nm = T_in * 0.112985;

    const F_max = (1 + vUncrt) * F_PL;
    const F_min = (1 - vUncrt - vRelax) * F_PL;

    const pct = Math.round(vPctYld * 100);
    const circumference = 2 * Math.PI * 65;
    const offset = circumference * (1 - vPctYld);
    const gaugeColor = pct > 85 ? '#e74c3c' : pct > 77 ? '#F0A500' : 'var(--accent)';

    setResults({
      pct, offset, gaugeColor,
      T_in, T_ft, T_nm, KT,
      F_PL, F_PL_kN: F_PL * 0.00444822,
      F_min, F_min_pct: (F_min / (vSty * vAt)) * 100,
      F_max, F_max_pct: (F_max / (vSty * vAt)) * 100,
      rt, rc, lam,
    });
  };

  const handleCalculate = () => runCalculate();

  const handleReset = () => {
    setBoltPreset('1/2-13');
    setDNom('0.500');
    setDMinor('0.4001');
    setTpi('13');
    setAt('0.1419');
    setMaterialPreset('SAE Grade 8');
    setSty('130000');
    setPctYld('0.67');
    setFrictionPreset('As-received, dry');
    setFt('0.15');
    setFc('0.15');
    setUncrt('0.25');
    setRelax('0.10');
    setResults(null);
    showToast('↺ Reset to default values');
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="btpc-page">

      {/* ─── HERO ─── */}
      <section className="btpc-hero">
        <div className="btpc-hero-inner">
          <nav className="btpc-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span className="btpc-breadcrumb-sep">›</span>
            <span>Engineering</span>
            <span className="btpc-breadcrumb-sep">›</span>
            <span>Bolt Torque &amp; Preload Calculator</span>
          </nav>
          <div className="btpc-hero-badge">⚙ Free Tool · No Login Required</div>
          <h1 className="btpc-hero-h1">
            Bolt Torque &amp;<br />
            <span className="btpc-accent">Preload Calculator</span>
          </h1>
          <p className="btpc-hero-lead">
            Calculate installation torque and preload force for UNC fasteners. Based on the
            Shigley/VDI formula using ASME B1.1 thread standards — used by mechanical engineers worldwide.
          </p>
          <div className="btpc-hero-tags">
            {['Shigley/VDI Formula', 'ASME B1.1', 'SAE & ISO Grades', 'lbf·in / lbf·ft / N·m', 'Min/Max Preload Range'].map(
              (t) => <span className="btpc-hero-tag" key={t}>{t}</span>
            )}
          </div>
        </div>
      </section>

      {/* ─── MAIN ─── */}
      <main className="btpc-main-wrap">

        {/* ─── CALCULATOR GRID ─── */}
        <div className="btpc-calc-grid">

          {/* LEFT: INPUTS */}
          <div className="btpc-panel">
            <div className="btpc-panel-header">
              <div className="btpc-panel-icon">⚙</div>
              <span className="btpc-panel-title">Input Parameters</span>
            </div>
            <div className="btpc-panel-body">

              {/* 1 · Bolt Size */}
              <div className="btpc-section-label">1 · Bolt Size</div>
              <div className="btpc-field" style={{ marginBottom: '12px' }}>
                <label className="btpc-label">Quick Select UNC Size</label>
                <select
                  className="btpc-select"
                  value={boltPreset}
                  onChange={(e) => applyBoltPreset(e.target.value)}
                >
                  <option value="">— Custom / Manual Entry —</option>
                  {UNC_SIZES.map((u) => (
                    <option key={u.s} value={u.s}>{u.s}</option>
                  ))}
                </select>
              </div>

              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Nominal Diameter <span className="btpc-sym">(d_nom)</span></label>
                  <input className="btpc-input" type="number" value={dNom} step="0.001" min="0.001"
                    onChange={(e) => { setDNom(e.target.value); setBoltPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">in</div>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Minor Diameter <span className="btpc-sym">(d_m)</span></label>
                  <input className="btpc-input" type="number" value={dMinor} step="0.0001" min="0.001"
                    onChange={(e) => { setDMinor(e.target.value); setBoltPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">in</div>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Threads Per Inch <span className="btpc-sym">(TPI)</span></label>
                  <input className="btpc-input" type="number" value={tpi} step="1" min="1"
                    onChange={(e) => { setTpi(e.target.value); setBoltPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">TPI</div>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Tensile Stress Area <span className="btpc-sym">(At)</span></label>
                  <input className="btpc-input" type="number" value={At} step="0.0001" min="0.0001"
                    onChange={(e) => { setAt(e.target.value); setBoltPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">in²</div>
              </div>

              {/* 2 · Material */}
              <div className="btpc-section-label">2 · Material</div>
              <div className="btpc-field" style={{ marginBottom: '12px' }}>
                <label className="btpc-label">Material Preset</label>
                <select className="btpc-select" value={materialPreset}
                  onChange={(e) => applyMaterialPreset(e.target.value)}>
                  <option value="">— Custom Entry —</option>
                  {MATERIALS.map((m) => (
                    <option key={m.g} value={m.g}>{m.g} — {m.sty.toLocaleString()} psi</option>
                  ))}
                </select>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Tensile Yield Strength <span className="btpc-sym">(Sty)</span></label>
                  <input className="btpc-input" type="number" value={Sty} step="1000" min="1000"
                    onChange={(e) => { setSty(e.target.value); setMaterialPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">psi</div>
              </div>

              {/* 3 · Preload Target */}
              <div className="btpc-section-label">3 · Preload Target</div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Preload — % of Yield <span className="btpc-sym">(%yld)</span></label>
                  <input className="btpc-input" type="number" value={pctYld} step="0.01" min="0.01" max="0.95"
                    onChange={(e) => setPctYld(e.target.value)} />
                </div>
                <div className="btpc-unit-badge">frac</div>
              </div>
              <div className="btpc-hint">Recommended: 0.64 – 0.77 (64–77% of yield)</div>

              {/* 4 · Friction */}
              <div className="btpc-section-label">4 · Friction</div>
              <div className="btpc-field" style={{ marginBottom: '12px' }}>
                <label className="btpc-label">Friction Preset</label>
                <select className="btpc-select" value={frictionPreset}
                  onChange={(e) => applyFrictionPreset(e.target.value)}>
                  <option value="">— Custom Entry —</option>
                  {FRICTION.map((f) => (
                    <option key={f.c} value={f.c}>{f.c} ({f.ft})</option>
                  ))}
                </select>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Thread Friction Coeff <span className="btpc-sym">(ft)</span></label>
                  <input className="btpc-input" type="number" value={ft} step="0.01" min="0.01" max="0.5"
                    onChange={(e) => { setFt(e.target.value); setFrictionPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">—</div>
              </div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Collar Friction Coeff <span className="btpc-sym">(fc)</span></label>
                  <input className="btpc-input" type="number" value={fc} step="0.01" min="0.01" max="0.5"
                    onChange={(e) => { setFc(e.target.value); setFrictionPreset(''); }} />
                </div>
                <div className="btpc-unit-badge">—</div>
              </div>

              {/* 5 · Uncertainty & Relaxation */}
              <div className="btpc-section-label">5 · Uncertainty &amp; Relaxation</div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Preload Uncertainty <span className="btpc-sym">(%uncrt)</span></label>
                  <input className="btpc-input" type="number" value={uncrt} step="0.01" min="0" max="0.9"
                    onChange={(e) => setUncrt(e.target.value)} />
                </div>
                <div className="btpc-unit-badge">frac</div>
              </div>
              <div className="btpc-hint">Torque wrench ≈ 0.25; DTI ≈ 0.10</div>
              <div className="btpc-field-row">
                <div className="btpc-field">
                  <label className="btpc-label">Preload Relaxation <span className="btpc-sym">(%relax)</span></label>
                  <input className="btpc-input" type="number" value={relax} step="0.01" min="0" max="0.5"
                    onChange={(e) => setRelax(e.target.value)} />
                </div>
                <div className="btpc-unit-badge">frac</div>
              </div>
              <div className="btpc-hint">Embedment relaxation; 10% general rule</div>

              <button className="btpc-btn-calc" onClick={handleCalculate}>
                <span>⚡</span> Calculate Torque &amp; Preload
              </button>
              <button className="btpc-btn-reset" onClick={handleReset}>Reset to Default Values</button>
            </div>
          </div>

          {/* RIGHT: RESULTS */}
          <div>
            {/* Results Panel */}
            <div className="btpc-panel" style={{ marginBottom: '16px' }}>
              <div className="btpc-panel-header">
                <div className="btpc-panel-icon">📊</div>
                <span className="btpc-panel-title">Results</span>
              </div>
              <div className="btpc-panel-body">
                {!results ? (
                  <div className="btpc-placeholder">
                    <div className="btpc-placeholder-icon">🔩</div>
                    <div className="btpc-placeholder-text">Enter inputs and click<br />Calculate to see results</div>
                  </div>
                ) : (
                  <div className="btpc-results-content">
                    {/* Gauge */}
                    <div className="btpc-gauge-wrap">
                      <div className="btpc-gauge-outer">
                        <svg className="btpc-gauge-svg" viewBox="0 0 160 160">
                          <circle className="btpc-gauge-track" cx="80" cy="80" r="65" />
                          <circle
                            className="btpc-gauge-fill"
                            cx="80" cy="80" r="65"
                            style={{
                              strokeDashoffset: results.offset,
                              stroke: results.gaugeColor,
                            }}
                          />
                        </svg>
                        <div className="btpc-gauge-center">
                          <div className="btpc-gauge-pct">{results.pct}%</div>
                          <div className="btpc-gauge-label">% of Yield</div>
                        </div>
                      </div>
                    </div>

                    {/* Primary torque */}
                    <div className="btpc-result-block btpc-highlight">
                      <div className="btpc-result-top">
                        <span className="btpc-result-name">Installation Torque</span>
                      </div>
                      <div className="btpc-result-value btpc-orange">{fmtComma(results.T_in, 1)}</div>
                      <div className="btpc-result-unit">lbf·in</div>
                    </div>

                    {/* 3-col mini */}
                    <div className="btpc-result-3col">
                      <div className="btpc-result-mini">
                        <div className="btpc-result-name">lbf·ft</div>
                        <div className="btpc-result-value">{fmt(results.T_ft, 1)}</div>
                      </div>
                      <div className="btpc-result-mini">
                        <div className="btpc-result-name">N·m</div>
                        <div className="btpc-result-value">{fmt(results.T_nm, 1)}</div>
                      </div>
                      <div className="btpc-result-mini">
                        <div className="btpc-result-name">KT Factor</div>
                        <div className="btpc-result-value">{fmt(results.KT, 4)}</div>
                      </div>
                    </div>

                    {/* Preload */}
                    <div className="btpc-result-block">
                      <div className="btpc-result-top">
                        <span className="btpc-result-name">Nominal Preload Force</span>
                      </div>
                      <div className="btpc-result-value">{fmtComma(results.F_PL, 1)} lbf</div>
                      <div className="btpc-result-unit">{fmt(results.F_PL_kN, 4)} kN</div>
                    </div>

                    {/* Preload range */}
                    <div className="btpc-divider-label">Preload Range</div>
                    <div className="btpc-minmax-grid">
                      <div className="btpc-minmax-item btpc-min-item">
                        <div className="btpc-minmax-label">⬇ Minimum (lbf)</div>
                        <div className="btpc-minmax-val btpc-warning">{fmtComma(results.F_min, 1)} lbf</div>
                        <div className="btpc-result-unit">{fmt(results.F_min_pct, 1)}% of yield</div>
                      </div>
                      <div className="btpc-minmax-item btpc-max-item">
                        <div className="btpc-minmax-label">⬆ Maximum (lbf)</div>
                        <div className="btpc-minmax-val btpc-orange">{fmtComma(results.F_max, 1)} lbf</div>
                        <div className="btpc-result-unit">{fmt(results.F_max_pct, 1)}% of yield</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Intermediate Values */}
            {results && (
              <div className="btpc-panel">
                <div className="btpc-panel-header">
                  <div className="btpc-panel-icon">🔬</div>
                  <span className="btpc-panel-title">Intermediate Values</span>
                </div>
                <div className="btpc-panel-body">
                  <div className="btpc-inter-grid">
                    <div className="btpc-inter-item">
                      <div className="btpc-inter-label">Mean Thread Radius rt</div>
                      <div className="btpc-inter-val">{fmt(results.rt, 6)} in</div>
                    </div>
                    <div className="btpc-inter-item">
                      <div className="btpc-inter-label">Mean Collar Radius rc</div>
                      <div className="btpc-inter-val">{fmt(results.rc, 6)} in</div>
                    </div>
                    <div className="btpc-inter-item">
                      <div className="btpc-inter-label">Lead Angle λ</div>
                      <div className="btpc-inter-val">{fmt(results.lam * 180 / Math.PI, 4)}°</div>
                    </div>
                    <div className="btpc-inter-item">
                      <div className="btpc-inter-label">Thread Half-Angle α</div>
                      <div className="btpc-inter-val">30°</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── FORMULAS & THEORY ─── */}
        <section className="btpc-formula-section">
          <h2 className="btpc-section-heading">Formulas &amp; Theory</h2>
          <p className="btpc-section-sub">
            This calculator applies the <strong>Shigley/VDI torque equation</strong>, the most widely used
            engineering method for torque-preload estimation, based on ASME B1.1 Unified thread geometry (α = 30°).
          </p>
          <div className="btpc-formula-cards">
            {[
              {
                title: 'Preload Force',
                eq: 'F_PL = %yld × Sty × At',
                desc: '<strong>%yld</strong> = fraction of yield (e.g. 0.70) · <strong>Sty</strong> = tensile yield strength (psi) · <strong>At</strong> = tensile stress area (in²). Recommended range: 64%–77% of yield.',
              },
              {
                title: 'Torque Coefficient KT',
                eq: 'KT = (1/d) × [rt(tanλ + ft/cosα) + fc·rc]',
                desc: 'Combines thread geometry and friction into a single nut factor. <strong>Default K ≈ 0.20</strong> for dry steel when friction data is unavailable.',
              },
              {
                title: 'Installation Torque',
                eq: 'T = KT × d_nom × F_PL',
                desc: 'Required wrench torque to achieve target preload. Output provided in <strong>lbf·in</strong>, <strong>lbf·ft</strong>, and <strong>N·m</strong>.',
              },
              {
                title: 'Preload Min / Max',
                eq: 'F_max = (1 + %uncrt) × F_nom\nF_min = (1 − %uncrt − %relax) × F_nom',
                desc: 'Accounts for torque wrench scatter (±25% typical) and embedment relaxation (10% default). Use DTIs or stretch gauges to reduce uncertainty.',
              },
            ].map((card) => (
              <div className="btpc-formula-card" key={card.title}>
                <h3>{card.title}</h3>
                <div className="btpc-formula-eq">{card.eq}</div>
                <p className="btpc-formula-desc" dangerouslySetInnerHTML={{ __html: card.desc }} />
              </div>
            ))}
          </div>
        </section>

        {/* ─── REFERENCE TABLES ─── */}
        <section className="btpc-tabs-section">
          <h2 className="btpc-section-heading" style={{ marginBottom: '20px' }}>Reference Tables</h2>
          <div className="btpc-tabs-header">
            {[
              { id: 'unc', label: 'UNC Bolt Sizes' },
              { id: 'materials', label: 'Bolt Materials' },
              { id: 'friction', label: 'Friction Coefficients' },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`btpc-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'unc' && (
            <div className="btpc-tab-panel active">
              <div className="btpc-ref-table-wrap">
                <table className="btpc-ref-table">
                  <thead>
                    <tr>
                      <th>Size</th><th>d_nom (in)</th><th>d_m (in)</th>
                      <th>TPI</th><th>P (in)</th><th>At (in²)</th><th>Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {UNC_SIZES.map((u) => (
                      <tr key={u.s}>
                        <td className="btpc-grade-tag">{u.s}</td>
                        <td>{u.d}</td>
                        <td>{u.dm}</td>
                        <td>{u.tpi}</td>
                        <td>{(1 / u.tpi).toFixed(5)}</td>
                        <td>{u.At}</td>
                        <td>
                          <button className="btpc-btn-use-row" onClick={() => applyUNC(u)}>Use</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="btpc-tab-panel active">
              <div className="btpc-ref-table-wrap">
                <table className="btpc-ref-table">
                  <thead>
                    <tr>
                      <th>Grade / Material</th><th>Sty (psi)</th>
                      <th>Sty (MPa)</th><th>Notes</th><th>Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MATERIALS.map((m) => (
                      <tr key={m.g}>
                        <td className="btpc-grade-tag">{m.g}</td>
                        <td>{m.sty.toLocaleString()}</td>
                        <td>{m.mpa}</td>
                        <td style={{ color: 'var(--text2)' }}>{m.note}</td>
                        <td>
                          <button className="btpc-btn-use-row" onClick={() => applyMaterial(m)}>Use</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'friction' && (
            <div className="btpc-tab-panel active">
              <div className="btpc-ref-table-wrap">
                <table className="btpc-ref-table">
                  <thead>
                    <tr>
                      <th>Surface Condition</th><th>ft (Thread)</th>
                      <th>fc (Collar)</th><th>KT (approx)</th><th>Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FRICTION.map((f) => (
                      <tr key={f.c}>
                        <td style={{ color: 'var(--text)' }}>{f.c}</td>
                        <td>{f.ft}</td>
                        <td>{f.fc}</td>
                        <td>{f.kt}</td>
                        <td>
                          <button className="btpc-btn-use-row" onClick={() => applyFrictionRow(f)}>Use</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* ─── FAQ ─── */}
        <section className="btpc-faq-section">
          <h2 className="btpc-section-heading">Frequently Asked Questions</h2>
          <p className="btpc-section-sub">Common questions about bolt torque, preload, and fastener engineering.</p>
          <div className="btpc-faq-list">
            {FAQS.map((f, i) => (
              <div className="btpc-faq-item" key={i}>
                <button
                  className={`btpc-faq-question${openFaq === i ? ' open' : ''}`}
                  onClick={() => toggleFaq(i)}
                >
                  <span>{f.q}</span>
                  <span className="btpc-faq-arrow">+</span>
                </button>
                {openFaq === i && (
                  <div className="btpc-faq-answer open">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

      </main>

      {/* ─── TOAST ─── */}
      <div className={`btpc-toast${toastVisible ? ' show' : ''}`}>{toast}</div>
    </div>
  );
}
